// Opening a hazard from a marker click: fetch hazard(id:), decide what the
// panel does with the answer. The network client, the error reporter and the
// analytics sink are injected so the decisions are tested with fakes.
//
//   shown  — an ACTIVE hazard: render it (and count one hazard_viewed)
//   gone   — null, or no longer ACTIVE: close the panel, say "This hazard has
//            already left the map." and refresh the markers
//   failed — the request threw: one handled-error report per failure, no spam
//   stale  — another marker was clicked while this one was loading: ignore it

import { GetHazardById } from '../graphql/hazard-graph';
import { Hazard, isHazardOnMap, normalizeHazard } from './hazardDetails';

export type HazardLoadResult =
  | { kind: 'shown'; hazard: Hazard }
  | { kind: 'gone' }
  | { kind: 'failed'; error: unknown }
  | { kind: 'stale' };

export interface GraphRequester {
  request(document: any, variables?: Record<string, unknown>): Promise<any>;
}

export type ErrorReporter = (error: unknown, context: Record<string, unknown>) => void;

export class HazardLoader {
  private latest = 0;

  constructor(
    private readonly client: GraphRequester,
    private readonly reportError: ErrorReporter,
  ) {}

  /** Invalidates any request in flight, e.g. when the panel is closed. */
  cancel(): void {
    this.latest += 1;
  }

  async load(id: string): Promise<HazardLoadResult> {
    const ticket = ++this.latest;
    try {
      const data = await this.client.request(GetHazardById, { id });
      if (ticket !== this.latest) return { kind: 'stale' };
      const hazard = normalizeHazard(data?.hazard);
      return isHazardOnMap(hazard) ? { kind: 'shown', hazard } : { kind: 'gone' };
    } catch (error) {
      if (ticket !== this.latest) return { kind: 'stale' };
      this.reportError(error, { component: 'trail_hazards', operation: 'fetch_hazard', hazard_id: id });
      return { kind: 'failed', error };
    }
  }
}
